import { useMemo } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';

import { useSubSideNavPathname } from '~/components/v2/components/workspace/sub-side-nav/useSubSideNavPathname';
import { useWorkspaceAgentPanelCanvasOpen } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useWorkspaceAgentPanelCanvasOpen';
import { Pathnames } from '~/components/v2/constants/pathnames';
import { Pathname } from '~/constants/pathname';
import { isWorkspaceAgentSkillsFullPageUiEnabled } from '~/constants/workspaceAgentSkillsFeature';
import { useFetchAnyWorkspacePermissions } from '~/hooks/useFetchAnyWorkspacePermissions';
import { usePrivateAddOnFeatures } from '~/hooks/usePrivateAddOnFeatures';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useCurrentWorkspaceAddOns } from '~/swr-hooks/workspaces/addOns/useCurrentWorkspaceAddOns';
import { isPathnameOrNested } from '~/utils/isPathnameOrNested';
import { SEARCH_SUB_SIDE_NAV_PATHNAMES } from '~/utils/isSearchSubSideNavPathname';
import {
  canManageAgentSessions,
  canViewAllAgentSessions as canViewAllAgentSessionsPermission,
} from '~/utils/permissions/workspacePermissions';

/**
 * asPath prefixes whose page chrome mounts `PrivateWorkspaceAgentHeaderTasksButton`.
 * Board and library are roots (`/b/:id`, `/l/:id`); skills joins when full-page skills UI is on
 * (`WorkspaceAgentSkillsHeaderActions` with Start task); automations list/detail when `automationsEnabled`
 * (`AutomationsHeaderActions`); search-family pages mount `SearchHeaderActions` (including nested asset
 * routes like `/all/c/:clipId` and `/saved-search/:id/c/:clipId`).
 * New pages stay hidden until they grow Start task chrome and join this list.
 */
export const WORKSPACE_AGENT_PANEL_HEADER_CHROME_PATHNAMES = [
  Pathnames.Board,
  Pathnames.Library,
  Pathnames.MyCanvases,
  Pathnames.RecentlyDeleted,
  Pathname.SavedSearch,
  ...SEARCH_SUB_SIDE_NAV_PATHNAMES,
] as const;

export const isWorkspaceAgentSkillsHeaderChromePathname = (pathname: string) =>
  isPathnameOrNested(pathname, Pathnames.Skills);

export const isWorkspaceAgentAutomationsHeaderChromePathname = (pathname: string) =>
  isPathnameOrNested(pathname, Pathnames.Automations);

/**
 * Central access rules for the workspace agent panel feature. Mount sites read derived flags
 * from here rather than checking permissions or flags directly. `canManageAgentSessions` is
 * read from `mode: 'any'` permissions so board guests and viewers can open the agent.
 *
 * `isWorkspaceAgentPanelProviderEnabled` — mount the provider (agentSessions add-on + manage).
 * Survives canvas open, non-chrome routes, and viewport stand-down so session state, WSS
 * subscriptions, and composer draft persist. Dock is left expanded so leaving a stand-down
 * route restores the panel if it was open.
 *
 * `isWorkspaceAgentPanelAvailable` — Tasks entry points and asset-view panel geometry (add-on +
 * environment + manage). Deliberately says nothing about `dock`, so it still holds while minimized.
 *
 * `isHeaderChromeRoute` — in-flight asPath is board, library, canvases, saved search, or another
 * search-family gallery. False on showcases, settings, and other private-layout pages with no Start task chrome.
 *
 * `isAgentSelectionBarEnabled` — agent selection bar path when the add-on is on (manage ignored).
 *
 * `canShowAgentStartTask` — selection-bar Start task CTA and pre-ui2026 idle FAB
 * (add-on + manage + above-medium). Header Start task is additionally gated on `ui2026` at
 * the chrome call sites, and on `isHeaderChromeRoute` via `isWorkspaceAgentPanelAvailable`.
 *
 * `canViewAllAgentSessions` — sysadmin view-all; task rows may show session creator names.
 *
 * `isWorkspaceAgentAccessResolved` — add-ons and any-mode workspace permissions have finished
 * loading for the current workspace, so provider enablement is definitive (not the transient false
 * while pending).
 */
export const useIsWorkspaceAgentPanelAvailable = () => {
  const { agentSessions: isAgentSessionsEnabled } = usePrivateAddOnFeatures();
  const { isPending: areAddOnsPending } = useCurrentWorkspaceAddOns({ enabled: true });
  const { canvasOpen } = useWorkspaceAgentPanelCanvasOpen();
  const { subSideNavPathname } = useSubSideNavPathname();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { isFetched: areWorkspacePermissionsFetched, data: workspacePermissions } = useFetchAnyWorkspacePermissions();
  const { currentWorkspace } = useCurrentWorkspace();
  const { automationsEnabled, enableSkillsUi, workspaceAgentSkillsEnabled } = useTemporaryFeatureFlags();

  const isHeaderChromeRoute = useMemo(() => {
    const isStaticHeaderChromeRoute = WORKSPACE_AGENT_PANEL_HEADER_CHROME_PATHNAMES.some((basePathname) =>
      isPathnameOrNested(subSideNavPathname, basePathname),
    );

    const isSkillsHeaderChromeRoute =
      isWorkspaceAgentSkillsFullPageUiEnabled({ enableSkillsUi, workspaceAgentSkillsEnabled }) &&
      isWorkspaceAgentSkillsHeaderChromePathname(subSideNavPathname);

    const isAutomationsHeaderChromeRoute =
      automationsEnabled && isWorkspaceAgentAutomationsHeaderChromePathname(subSideNavPathname);

    return isStaticHeaderChromeRoute || isSkillsHeaderChromeRoute || isAutomationsHeaderChromeRoute;
  }, [automationsEnabled, enableSkillsUi, subSideNavPathname, workspaceAgentSkillsEnabled]);
  const hasManagePermission = canManageAgentSessions(workspacePermissions);
  const passesEnvironment = isAboveMediumScreen && !canvasOpen && isHeaderChromeRoute;
  const agentSessionsEnabled = isAgentSessionsEnabled === true;
  const isWorkspaceAgentAccessResolved =
    Boolean(currentWorkspace) &&
    !areAddOnsPending &&
    isAgentSessionsEnabled !== undefined &&
    areWorkspacePermissionsFetched;

  return {
    isWorkspaceAgentAccessResolved,
    isWorkspaceAgentPanelProviderEnabled: agentSessionsEnabled && hasManagePermission,
    isWorkspaceAgentPanelAvailable: agentSessionsEnabled && passesEnvironment && hasManagePermission,
    isHeaderChromeRoute,
    isAgentSelectionBarEnabled: agentSessionsEnabled,
    canShowAgentStartTask: agentSessionsEnabled && hasManagePermission && isAboveMediumScreen,
    canViewAllAgentSessions: canViewAllAgentSessionsPermission(workspacePermissions),
  };
};
